import type { ApiClient } from "@fieldcheck/client";
import { toSyncError } from "@fieldcheck/powersync";
import { BasePowerSyncConnector, type CrudOperation, type TokenStorage } from "./base-connector";
import { HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from "../constants/common";

/** Total attempts (1 initial + retries) for a TRANSIENT token fetch failure. */
const DEFAULT_MAX_TOKEN_FETCH_ATTEMPTS = 3;
/** Base backoff between transient retries; doubled each attempt. */
const DEFAULT_TOKEN_FETCH_RETRY_MS = 500;
/** Server errors at/above this are transient (retryable), unlike 4xx. */
const HTTP_SERVER_ERROR_MIN = 500;

export interface FieldcheckConnectorConfig {
  /** PowerSync Instance URL */
  endpoint: string;
  /** Shared token storage implementation */
  storage: TokenStorage;
  /** Pre-configured OpenAPI client */
  apiClient: ApiClient;
  /** Callback when auth errors (401/403) occur to notify session machines */
  onAuthError?: (error: unknown) => void;
  /** Optional guard to skip network calls if we know we are visitors */
  shouldFetchToken?: () => boolean;
  /**
   * Namespaces the cached JWT keys per account (multi-account). When omitted,
   * the legacy single-account keys are used.
   */
  cacheNamespace?: string;
  /**
   * Total attempts for a TRANSIENT `get-session` failure (network error / 5xx)
   * before giving up and returning `null`. A real 401/403 is NEVER retried.
   * Defaults to {@link DEFAULT_MAX_TOKEN_FETCH_ATTEMPTS}. Exposed for tests.
   */
  maxTokenFetchAttempts?: number;
  /**
   * Base backoff (ms) between transient retries; doubled each attempt. Exposed
   * for tests (set to `0` to retry without real delay).
   */
  tokenFetchRetryBaseMs?: number;
  /**
   * Multi-account upload gate (CC-42): return `false` while this connector's
   * account is NOT the tab's active API identity so its queued CRUD is deferred
   * instead of being uploaded (and silently dropped/misattributed) under the
   * OTHER account's credentials. See `ConnectorConfig.canUploadNow`.
   */
  canUploadNow?: () => boolean;
}

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Shared PowerSync Connector for the Fieldcheck ecosystem.
 * Consolidates common auth and batch upload logic used by both Web and Native apps.
 */
export class FieldcheckPowerSyncConnector extends BasePowerSyncConnector {
  constructor(fieldcheckConfig: FieldcheckConnectorConfig) {
    super({
      endpoint: fieldcheckConfig.endpoint,
      storage: fieldcheckConfig.storage,
      cacheNamespace: fieldcheckConfig.cacheNamespace,
      canUploadNow: fieldcheckConfig.canUploadNow,
      fetchAuthToken: async () => {
        if (fieldcheckConfig.shouldFetchToken && !fieldcheckConfig.shouldFetchToken()) {
          return null;
        }

        const maxAttempts =
          fieldcheckConfig.maxTokenFetchAttempts ?? DEFAULT_MAX_TOKEN_FETCH_ATTEMPTS;
        const retryBaseMs = fieldcheckConfig.tokenFetchRetryBaseMs ?? DEFAULT_TOKEN_FETCH_RETRY_MS;

        // Bounded retry/backoff so a TRANSIENT `get-session` failure (a network
        // blip or a 5xx) does NOT immediately surrender a `null` token — which
        // would leave PowerSync without credentials and the sync stuck
        // "Checking…". We DISTINGUISH error classes:
        //   • 401/403  → the session is genuinely invalid: notify the session
        //                machine and return `null` WITHOUT retrying.
        //   • network / 5xx → transient: retry with exponential backoff.
        // After exhausting the budget we return `null` (the caller still has its
        // valid-cache fallback) — never an infinite loop.
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          try {
            console.log("[PowerSync] Fetching auth token...");
            const { data, error, response } = await fieldcheckConfig.apiClient.GET(
              "/api/auth/get-session",
              {
                params: { query: { jwt: true } },
              },
            );

            const { ok, status } = response;

            if (ok && data) {
              return data.token ?? null;
            }

            if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
              console.warn("[PowerSync] Auth failed (401/403).");
              // Ensure the status is included so the session machine can recognize the auth failure
              const authError =
                error && typeof error === "object"
                  ? { ...error, status }
                  : { message: "Unauthorized", status };
              fieldcheckConfig.onAuthError?.(authError);
              return null;
            }

            // A non-auth, non-5xx, non-OK response (e.g. a 4xx other than
            // 401/403, or an OK response missing `data`) is not something a
            // retry will fix — give up immediately.
            if (status < HTTP_SERVER_ERROR_MIN) {
              console.warn(`[PowerSync] Token fetch failed (status ${status}); not retrying.`);
              return null;
            }

            console.warn(
              `[PowerSync] Token fetch transient failure (status ${status}); attempt ${attempt + 1}/${maxAttempts}.`,
            );
          } catch (err) {
            // A thrown error is a network failure (fetch rejected): transient.
            console.warn(
              `[PowerSync] Token fetch network error; attempt ${attempt + 1}/${maxAttempts}.`,
              err,
            );
          }

          const isLastAttempt = attempt === maxAttempts - 1;
          if (!isLastAttempt) {
            // oxlint-disable-next-line no-await-in-loop -- sequential backoff is intentional
            await sleep(retryBaseMs * 2 ** attempt);
          }
        }

        console.error("[PowerSync] Token fetch exhausted retries; returning null.");
        return null;
      },
      uploadBatch: async (operations: CrudOperation[]) => {
        const headers: Record<string, string> = {};
        if (fieldcheckConfig.cacheNamespace) {
          headers["X-Active-Account"] = fieldcheckConfig.cacheNamespace;
        }
        const result = await fieldcheckConfig.apiClient.POST("/api/sync/batch", {
          body: { operations },
          headers,
        });

        if (result.error) {
          // El STATUS HTTP viaja con el error hasta `classifyError`. Serializar
          // el cuerpo a texto lo tiraba, y el clasificador acababa adivinando
          // por subcadenas: un `404` dentro de la prosa de un 503 (un
          // `Retry-After: 3404`, un id de incidente) se leía como conflicto y
          // DESCARTABA las escrituras encoladas del usuario.
          throw toSyncError(result.error, result.response.status);
        }
      },
    });
  }
}
